import React, { useRef, useState, useCallback } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faMicrophone, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import VoiceService from "../../services/voiceService";
import useMicrophone from "../../hooks/useMicrophone";

interface ChatInputProps {
    value: string;
    loading: boolean;
    onChange: (value: string) => void;
    onSend: () => void;
}

export default function ChatInput({
    value,
    loading,
    onChange,
    onSend,
}: ChatInputProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { start: startMic, stop: stopMic } = useMicrophone();
    const [isRecording, setIsRecording] = useState(false);

    // Read inside the dictation callback so each transcript appends onto
    // whatever is currently in the box, not a stale value from when
    // recording started.
    const valueRef = useRef(value);
    valueRef.current = value;

    const handleChange = (
        e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        onChange(e.target.value);

        const textarea = e.target;

        textarea.style.height = "auto";
        textarea.style.height =
            Math.min(textarea.scrollHeight, 180) + "px";
    };

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLTextAreaElement>
    ) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();

            if (!loading && value.trim()) {
                onSend();
            }
        }
    };

    const appendTranscript = useCallback((text: string) => {
        const current = valueRef.current;
        onChange(current ? `${current} ${text}` : text);
    }, [onChange]);

    const toggleRecording = async () => {
        if (isRecording) {
            await stopMic();
            VoiceService.disconnect();
            setIsRecording(false);
            return;
        }

        VoiceService.connect(
            (message) => {
                if (message.type === "transcript" && message.text) {
                    appendTranscript(message.text);
                }
            },
            () => {}, // dictation mode never sends audio back
            "/ws/dictate"
        );

        await startMic();
        setIsRecording(true);
    };

    return (
        <div className="chat-input-wrapper">

            <div className="chat-input-card">

                <button
                    className="chat-tool-btn"
                    title="Attach (Coming Soon)"
                >
                    <FontAwesomeIcon icon={faPaperclip} />
                </button>

                <textarea
                    ref={textareaRef}
                    className="chat-textarea"
                    rows={1}
                    placeholder="Ask Menti anything about your chapter..."
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                />

                <button
                    type="button"
                    className={`chat-tool-btn${isRecording ? " recording" : ""}`}
                    title={isRecording ? "Stop recording" : "Voice input"}
                    onClick={toggleRecording}
                >
                    <FontAwesomeIcon icon={faMicrophone} />
                </button>

                <button
                    className="send-btn"
                    disabled={loading || !value.trim()}
                    onClick={onSend}
                >
                    {loading ? (
                        <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                        <FontAwesomeIcon icon={faPaperPlane} />
                    )}
                </button>

            </div>

            <div className="input-footer">

                Press <strong>Enter</strong> to send •
                <strong> Shift + Enter</strong> for new line

            </div>

        </div>
    );
}
